import { useEffect } from "react";
import { isHostLoggedOut } from "@/utils/host-login-state";
import { redirectToHostLogin } from "@/utils/host-login-redirect";

/**
 * 宿主未登录守卫。
 *
 * 用于「必须登录才能看」的页面：宿主（小程序）已声明未登录时，把用户交还给小程序登录页，
 * 并告知调用方不要渲染任何内容。
 *
 * 为什么需要它：H5 侧 goToPage / goToPageV2 拼小程序跳转时 appId 恒为空，小程序的
 * getCode() 登录门禁完全不会执行。于是「退登 → 游客态专题页 → 跳其它充电 H5」这条路径
 * 会在未登录状态下直接打开这些页面，而 webview 内的 cookie 仍是退登前的旧 uid，
 * 不拦就会串用上一个用户的登录态。
 *
 * 参数缺失（APP / 外部浏览器 / 未升级的小程序）时 isHostLoggedOut() 恒为 false，
 * 本 hook 不做任何事，各页面沿用原有登录逻辑，零回归。
 *
 * @param options.skip 跳过守卫，用于支持游客访问的路由（专题页有自己的 guest 降级逻辑）
 * @returns true 表示宿主未登录、已发起跳转，调用方应停止渲染内容
 */
export const useHostLoginGuard = (options?: { skip?: boolean }): boolean => {
  const skip = Boolean(options?.skip);
  const blocked = !skip && isHostLoggedOut();

  useEffect(() => {
    if (!blocked) {
      return;
    }
    // replace=true：用 redirectTo 把当前页替换成登录页，而不是压栈。
    //
    // 被守卫拦住的页面是「中间页」——用户是从上游页面（通常是免登专题页）跳进来的，
    // 登录完还要返回那里。而小程序登录页只会通知 pages[pages.length - 2]，也就是
    // 「登录页的上一页」：压栈时那是本页自己，上游页收不到信号、不会重建 webview，
    // 于是「专题页 → 二级页登录 → 返回」时专题页仍停在游客态。替换后上一页变成上游页，
    // 信号才送得到它。这也是 4dc8d98 之前联登 SDK 的行为，那时这条链路是正常的。
    //
    // forceLogin=false：本页已被登录页替换掉，「需要登录的页面取消登录则退出」这一条
    // 已经天然满足；而收到信号的上游页该不该退出，要由它自己的性质决定——小程序侧
    // webpage 页的 this.isConfigPage 本来就有这个判断，免登专题页降级游客态留在原地，
    // 其它必登 H5 则退出。传 true 会把免登专题页也一起退掉，与产品预期相反。
    redirectToHostLogin(false, { replace: true });
  }, [blocked]);

  return blocked;
};

export default useHostLoginGuard;
